import React from 'react';
import { useQuery } from 'react-query';

import * as env from 'config/environment';
import * as api from 'config/api';
import * as consts from 'config/consts';

import { ReportArticle } from 'config/api';
import { Frequency, reportLoadingStyle } from 'pages/NewReport';
import { formatDate } from 'utils/formatter';
import { LineNoticeBanner } from 'components/features/home/HomeContentsInternal';
import Loading from 'components/commons/loading/Loading';
import { isPremium } from 'components/features/utils/userStatus';
import { AssetChange } from './components/AssetChange';
import { AssetChangePerAccount } from './components/AssetChangePerAccount';
import { ColumnTable } from './components/ColumnTable';
import { ManagedAssetsReport } from './components/ManagedAssetsReport';
import { TargetAchievementList } from './components/TargetAchievementList';
import { AssetTransition } from './components/AssetTransition';
import { Dividend } from './components/Dividend';
import {
  IsLinkedLineProps,
  ItemMaskCheck,
  ReportBaseProps,
  getQueryPeriod,
} from '../weekly-report/WeeklyReport';
import { yyyymmddToDate } from '../daily-report/DailyReport';

// 実装 ================================================================================================================================

const APIFail: React.FC<Record<string, unknown>> = () => {
  return (
    <>
      <div className="bg-white">
        <div className="flex flex-col items-center justify-start py-8 px-6">
          <div className="mb-6 text-lg text-center font-bold text-bluegray-700">
            準備中です
          </div>
          <img src="/static/images/report/report_stand-by.svg" alt="" />
          <div className="w-full mt-6 mb-10 mx-auto text-left text-xs text-black">
            初回月次レポートは来月２日に配信されます。
            <br />
            もうしばらくお待ち下さい。
          </div>
          <a id="monthlyreport_line" href={env.lineAddFriendURL}>
            <img
              src="/static/images/line1.png"
              alt="友だち追加"
              className="h-12.5"
              style={{ width: '265px' }}
            />
          </a>
        </div>
      </div>
    </>
  );
};

type MonthlyReportProps = {
  userName: string;
  setQuery: (q: string, frequency: Frequency) => void;
};

export type ReportResult = {
  CreatedAt: string;
  PreviousQuery: string;
  NextQuery: string;
  Base: ReportBaseProps;
  Articles: ReportArticle[];
};

export type ReportResultProps = {
  report: ReportResult;
};

const MonthlyReportInternal = (
  props: MonthlyReportProps & IsLinkedLineProps & ReportResultProps
) => {
  const { isLinkedLine, userName, setQuery, report } = props;

  const queryFrom = formatDate(
    yyyymmddToDate(report.Base.From.toString()),
    'yyyy/MM/dd'
  );
  const queryTo = formatDate(
    yyyymmddToDate(report.Base.To.toString()),
    'yyyy/MM/dd'
  );

  const from = report.Base.From !== undefined ? report.Base.From : 0;
  const to = report.Base.To !== undefined ? report.Base.To : 0;
  const isItemMask = ItemMaskCheck(from, to);

  const base_ = report.Base as ReportBaseProps;
  const queryPeriod = getQueryPeriod(base_.From, base_.To);

  return (
    <>
      <div className="">
        <div className="bg-white">
          <AssetChange
            {...{
              Base: base_,
              CreatedAt: report.CreatedAt,
              NextQuery: report.NextQuery,
              PreviousQuery: report.PreviousQuery,
              setQuery,
              isItemMask,
            }}
          />
          {!isItemMask && (
            <>
              <div className="p-4 overflow-hidden">
                <AssetTransition
                  {...{
                    previousTotalAssetAmount: base_.PreviousTotalAssetAmount,
                    queryPeriod,
                    Base: base_,
                    userName,
                  }}
                />
              </div>
              {!isLinkedLine && isPremium() && (
                <LineNoticeBanner
                  {...{ className: ['mb-2', 'w-1/2', 'w-3/4'] }}
                />
              )}
              <AssetChangePerAccount {...queryPeriod} />
            </>
          )}
        </div>
        <div className="">
          <Dividend {...{ from: base_.From, queryPeriod }} />
          <TargetAchievementList
            {...{
              from: base_.From,
              queryPeriod,
              userName,
            }}
          />
          <ManagedAssetsReport
            {...{
              from: base_.From,
              queryFrom,
              queryTo,
            }}
          />
          <ColumnTable
            {...{ Articles: report.Articles, userName, from: base_.From }}
          />
        </div>
      </div>
    </>
  );
};

export const MonthlyReport: React.FC<IsLinkedLineProps & {
  userName: string;
  query: string | null;
  setQuery: (q: string, frequency: Frequency) => void;
}> = props => {
  const query = props.query ?? undefined;
  const { data: reportData, isLoading, isRefetching } = useQuery(
    'monthlyReport',
    () => fetchReport(query),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (reportData === null || reportData === undefined)
    return <Loading style={reportLoadingStyle} loading={true} />;

  if (reportData.Month === null) return <APIFail />;

  if (
    isLoading ||
    isRefetching ||
    reportData.Month.Base.From === null ||
    reportData.Month.Base.To === null ||
    reportData.Month.NextQuery === undefined ||
    reportData.Month.PreviousQuery === undefined ||
    reportData.Month.Articles === null ||
    reportData.Month.Articles === undefined
  )
    return <Loading style={reportLoadingStyle} loading={true} />;

  return (
    <MonthlyReportInternal
      {...{
        report: reportData.Month as ReportResult,
        isLinkedLine: props.isLinkedLine,
        userName: props.userName,
        setQuery: props.setQuery,
      }}
    />
  );
};

const fetchReport = async (query: string | undefined) => {
  return await api.getReport(consts.reportType.月次, query);
};
